import { addGeneralError } from 'client/actions/error'
import { Segment } from 'client/Global/Segment'
import { SegmentEvents } from 'client/Global/SegmentEvents'
import { ApiError } from 'client/Interfaces/ErrorInterfaces'

import * as api from '../api/subscriptionManagement'
import { ICoupon, SubscriptionInterface, SubscriptionPlanInterface } from '../Interfaces/Subscription'
import { actionCreator, actionCreatorAsync, AsyncAction } from './'
import { ErrorActions, SubscriptionActions } from './actionTypes'
import { getCustomers } from './customer'

enum SubscriptionChangeType {
  paySubscriptionWithSource,
  updateSubscriptionPlan,
}

export function getPlans(): AsyncAction<SubscriptionPlanInterface[]> {
  return actionCreatorAsync<SubscriptionPlanInterface[]>(
    SubscriptionActions.LOAD_AVAILABLE_PLANS,
    () => api.getAvailablePlans(),
    addGeneralError,
  )
}

export function paySubscriptionWithSource(
  planId: string,
  sourceId: string,
  invoiceEmail: string,
  type: 'card' | 'sepa',
  captchaToken: string,
  captchaAction: string,
  couponCode?: string,
  isNotVATLiable?: boolean,
) {
  return async (dispatch) => {
    await dispatch(
      actionCreatorAsync(
        SubscriptionActions.PAY_SUBSCRIPTION,
        () =>
          api.postSubscription(
            planId,
            sourceId,
            type,
            invoiceEmail,
            captchaToken,
            captchaAction,
            couponCode,
            isNotVATLiable,
          ),
        (error: ApiError) => {
          if (error.status === 403 && error.message === 'Captcha error') {
            Segment.track(SegmentEvents.subscription.captchaError, { captchaAction, captchaToken })
          }
          const type =
            // eslint-disable-next-line no-nested-ternary
            error.status === 400
              ? SubscriptionActions.ALREADY_SUBSCRIBED
              : error.status === 402
                ? SubscriptionActions.PAYMENT_DECLINED
                : ErrorActions.ADD_ERROR
          return { type, payload: error }
        },
      ),
    )
    await dispatch(subscriptionChanged(SubscriptionChangeType.paySubscriptionWithSource))
  }
}

export function updateSubscriptionPlan(planId: string, isNotVATLiable: boolean, couponCode?: string) {
  return async (dispatch) => {
    await dispatch(
      actionCreatorAsync(
        SubscriptionActions.PAY_SUBSCRIPTION,
        () => api.putSubscription(planId, isNotVATLiable, couponCode),
        (error: ApiError) => {
          const type = error.status ? SubscriptionActions.CANCEL_FAILED : ErrorActions.ADD_ERROR
          return { type, payload: error }
        },
      ),
    )
    await dispatch(subscriptionChanged(SubscriptionChangeType.updateSubscriptionPlan))
  }
}

export function continueCurrentPlan(): AsyncAction<{ subscription: SubscriptionInterface }> {
  return actionCreatorAsync(
    SubscriptionActions.CONTINUE_CURRENT_PLAN,
    () => api.continueCurrentPlan(),
    (error: ApiError) => {
      const type = error.status === 402 ? SubscriptionActions.CANCEL_FAILED : ErrorActions.ADD_ERROR
      return { type, payload: error }
    },
  )
}

export function getSubscription() {
  return actionCreatorAsync(SubscriptionActions.LOAD_SUBSCRIPTION, () => api.getSubscription(), addGeneralError)
}

export function getSubscriptionSettings() {
  return actionCreatorAsync(SubscriptionActions.LOAD_SETTINGS, () => api.getSubscriptionSettings(), addGeneralError)
}

export function updateAccountingSubscription() {
  return actionCreatorAsync(
    SubscriptionActions.UPDATE_ACCOUNTING_SUBSCRIPTION,
    () => api.updateAccountingSubscription(),
    addGeneralError,
  )
}

export type SubscriptionSettingsItems =
  | 'consumablesTooltipEnabled'
  | 'extraObpFreebies'
  | 'monthlySubCancelDiscountUsed'
  | 'tinkPaymentEnabled'
  | 'updatePaymentCardModalShown'
  | 'yearlySubCancelDiscountUsed'

export function updateSubscriptionSettings(
  settingsItem: SubscriptionSettingsItems,
  settingsValue: boolean | string | number,
) {
  return async (dispatch) => {
    await dispatch(
      actionCreatorAsync(
        SubscriptionActions.UPDATE_SETTINGS,
        () => api.postSubscriptionSettingsChanges({ settingsItem, settingsValue }),
        addGeneralError,
      ),
    )
    await dispatch(getSubscriptionSettings())
  }
}

export function getInvoices() {
  return actionCreatorAsync(SubscriptionActions.LOAD_INVOICES, () => api.getInvoices(), addGeneralError)
}

export function updatePaymentMethod(
  sourceId: string,
  type: 'card' | 'sepa',
  captchaToken: string,
  captchaAction: string,
): AsyncAction<any> {
  return actionCreatorAsync(
    SubscriptionActions.UPDATE_PAYMENT_TOKEN,
    () => api.putPaymentSource(sourceId, type, captchaToken, captchaAction),
    (error: ApiError) => {
      return { type: SubscriptionActions.UPDATE_PAYMENT_ERROR, payload: error }
    },
  )
}

export function payUnpaidInvoices(): AsyncAction<any> {
  return actionCreatorAsync(
    SubscriptionActions.PAY_INVOICES,
    () => api.postPayInvoices(),
    (error: ApiError) => {
      return { type: SubscriptionActions.UPDATE_PAYMENT_ERROR, payload: error }
    },
  )
}

export const cancelSubscriptionConfirmation = actionCreator(SubscriptionActions.CONFIRM_CANCEL_SUBSCRIPTION)
export const removeCancelSubscriptionConfirmation = actionCreator(
  SubscriptionActions.REMOVE_CANCEL_SUBSCRIPTION_CONFIRMATION,
)

export function cancelSubscription(): AsyncAction<SubscriptionInterface> {
  return actionCreatorAsync(
    SubscriptionActions.CANCEL_SUBSCRIPTION,
    () => api.cancelSubscription(),
    (error: ApiError) => ({ type: SubscriptionActions.CANCEL_FAILED, payload: error }),
  )
}

export function refreshQuota() {
  return actionCreatorAsync(SubscriptionActions.LOAD_COMPANY_QUOTA, () => api.getQuota(), addGeneralError)
}

export function getCoupon(couponCode: string, planName?: string): AsyncAction<ICoupon> {
  return actionCreatorAsync(
    SubscriptionActions.COUPON.GET,
    () => api.getCoupon(couponCode, planName),
    (error: ApiError) => {
      const type = error.status === 404 ? SubscriptionActions.COUPON.FAILED : ErrorActions.ADD_ERROR
      return { type, payload: error }
    },
  )
}

export const clearCoupon = actionCreator(SubscriptionActions.COUPON.CLEAR)

export const removePaymentError = actionCreator(SubscriptionActions.CLEAR_ERROR)

export function subscriptionChanged(type: SubscriptionChangeType) {
  return getCustomers()
  // return (dispatch) => {}
}

export function applyChurnDiscount() {
  return async (dispatch) => {
    await dispatch(
      actionCreatorAsync(
        SubscriptionActions.APPLY_CHURN_DISCOUNT,
        () => api.postSubscriptionDiscount(),
        addGeneralError,
      ),
    )
    await dispatch(getSubscription())
  }
}
